import { useRef, useState } from "react";
import { Volume2, Loader2 } from "lucide-react";
import AdelaidaPortrait, { PHOTO_FRAMES, MALE_FRAMES } from "@/components/AdelaidaPortrait";
import { useOrbVoice } from "@/lib/orbVoice";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

// Landing "Meet your coach": Adelaida first, with a quiet switch to Richard; tap to hear a spoken hello, lips synced.
export default function LandingCoach() {
  const { amplitudeRef, speak, stop } = useOrbVoice("sage", "landing");
  const speakingRef = useRef(false);
  const [coach, setCoach] = useState(() => { try { return localStorage.getItem("xc_coach_pick") === "richard" ? "richard" : "adelaida"; } catch { return "adelaida"; } });
  const [state, setState] = useState("idle"); // idle | loading | speaking
  const name = coach === "richard" ? "Richard" : "Adelaida";
  const halt = () => { stop(); speakingRef.current = false; setState("idle"); };
  const tap = async () => {
    if (state === "speaking") { halt(); return; }
    if (state === "loading") return;
    setState("loading");
    try {
      const { data } = await api.get("/public/coach-hello", { params: { coach } });
      speakingRef.current = true; setState("speaking");
      await speak(null, data.audio);
    } catch { toast.error(`${name}'s voice isn't available just now.`); }
    speakingRef.current = false; setState("idle");
  };
  const switchTo = (c) => { halt(); setCoach(c); try { localStorage.setItem("xc_coach_pick", c); } catch {} };
  return (
    <div className="flex flex-col items-center">
      <button type="button" onClick={tap} className="relative block group" aria-label={`Hear ${name} say hello`} data-testid="landing-coach-portrait" data-coach={coach}>
        <div className="absolute -inset-10 rounded-full blur-3xl opacity-60 pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.45), transparent 70%)" }} />
        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 shadow-2xl transition-transform duration-500 group-hover:scale-[1.015]" style={{ width: "min(68vw, 340px)", aspectRatio: "3 / 4" }}>
          <AdelaidaPortrait key={coach} frames={coach === "richard" ? MALE_FRAMES : PHOTO_FRAMES} amplitudeRef={amplitudeRef} speakingRef={speakingRef} status={state === "speaking" ? "speaking" : "idle"} />
          <span className="absolute bottom-4 inset-x-0 flex justify-center" data-testid="landing-coach-hint">
            <span className="flex items-center gap-2 rounded-full bg-black/45 backdrop-blur-md px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-white/85">
              {state === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className={`w-3.5 h-3.5 ${state === "speaking" ? "animate-pulse" : ""}`} />}
              {state === "speaking" ? "Tap to stop" : `Tap to hear ${name}`}
            </span>
          </span>
        </div>
      </button>
      <button type="button" onClick={() => switchTo(coach === "richard" ? "adelaida" : "richard")} data-testid="landing-coach-switch"
        className="mt-5 text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
        {coach === "richard" ? "Back to Adelaida" : "Prefer a male coach? Meet Richard"}
      </button>
    </div>
  );
}
